import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import backarrow from "../../../assets/images/back-arrow-left.svg";
import closeicon from "../../../assets/images/close.svg";
import useDomesticHelperFlow from "../../../hooks/useDomesticHelperFlowSimple";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";

const DomesticHelperJourneyLayout = ({
  children,
  is_hide_back_btn,
  show_close_btn,
  onClose,
  customBackHandler,
  isFirstPage = false, // New prop to determine if it's the first page (show close) or not (show back)
}) => {
  const navigate = useNavigate();
  const { handlePrevSubstep } = useDomesticHelperFlow();
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  // Portal the mobile back button into a stable in-tree node provided by
  // the host page (#dh-back-portal in NewDomesticHelperPolicy), so an
  // animating ancestor cannot become the containing block for this fixed
  // button. Falls back to inline render when no target is present.
  // Placed before paint rather than after, so the button is never shown for a
  // frame inside the transformed page wrapper first. Same reason as the footer
  // in DomesticHelperBottomStickyBtnLayout.
  const [backPortalTarget, setBackPortalTarget] = useState(null);
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    setBackPortalTarget(document.getElementById("dh-back-portal"));
  }, []);

  const handleNavigationClick = () => {
    if (customBackHandler) {
      customBackHandler();
    } else if (onClose) {
      onClose();
    } else if (isFirstPage) {
      navigate("/new-policy");
    } else {
      handlePrevSubstep();
    }
  };

  return (
    <div className="">
      {/* Mobile Navigation - transparent spacer keeps content clear of the fixed header */}
      <div className="block md:hidden h-14" aria-hidden="true"></div>
      {/* Mobile Navigation - Fixed close/back button (visible <=767px).
          Portaled to #dh-back-portal in NewDomesticHelperPolicy so an
          animating ancestor (e.g. framer-motion page-transition wrapper
          that sets `transform`) can't become the containing block for
          this position:fixed button. */}
      {(!is_hide_back_btn || isFirstPage || show_close_btn) && (() => {
        const _mobileBack = (
          <div className="block md:hidden">
            <button
              type="button"
              className={`flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 shadow-sm transition-colors fixed top-3 z-[100] ${
                isRTL ? "right-4" : "left-4"
              }`}
              aria-label={
                isFirstPage || show_close_btn
                  ? translate("Close", "إغلاق")
                  : translate("Back", "رجوع")
              }
              onClick={handleNavigationClick}
            >
              {isFirstPage || show_close_btn ? (
                <img src={closeicon} alt="Close" className="w-5 h-5" />
              ) : (
                <img
                  src={backarrow}
                  alt="Back"
                  className={`w-5 h-5 ${isRTL ? "rotate-180" : ""}`}
                />
              )}
            </button>
          </div>
        );
        return backPortalTarget
          ? createPortal(_mobileBack, backPortalTarget)
          : _mobileBack;
      })()}

      {/* Desktop Navigation (visible >767px) */}
      <div className="block max767:hidden">
        {!is_hide_back_btn && (
          <button
            className="backButton bg-navy-blue rounded mt-5 flex absolute left-[26px] rtl:right-[26px] rtl:left-auto top-0 text-black text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
            onClick={customBackHandler || handlePrevSubstep}
          >
            <img
              src={backarrow}
              alt="Back arrow"
              className="rtl:ml-[5px] rtl:rotate-[181deg]"
            />
            <span className="back-text inline">
              {translate("Back", "رجوع")}
            </span>
          </button>
        )}
        {show_close_btn && (
          <button
            className="py-2 hidden rounded mt-4 flex absolute top-0 text-black text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                navigate("/new-policy");
              }
            }}
          >
            <img src={closeicon} alt="Close Icon" />
          </button>
        )}
      </div>

      {/* Content with mobile padding */}
      <div className="max767:pt-8">
        {" "}
        {/* Increased padding to prevent overlap with fixed nav + stepper */}
        {children}
      </div>
    </div>
  );
};

export default DomesticHelperJourneyLayout;
